import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { createLead } from '../lib/marketingApi';
import { isValidPhone } from '../lib/phone';
import { SEGMENT_OPTIONS, INTEREST_OPTIONS, PORTFOLIO_OPTIONS } from '../data/content';
import { Btn } from './ui';
import CaptchaWidget from './CaptchaWidget';
import { cn } from '@/lib/utils';

const inputBase =
  'w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-white/15 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-slate-500';
const labelBase = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200';

function getUtm() {
  if (typeof window === 'undefined') return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get('utm_source') || undefined,
    utm_medium: p.get('utm_medium') || undefined,
    utm_campaign: p.get('utm_campaign') || undefined,
  };
}

export default function LeadForm({
  source = 'website',
  defaultSegment = '',
  variant = 'full', // 'full' | 'compact'
  callbackDefault = false,
  submitLabel,
  className,
}) {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '', job_title: '',
    segment: defaultSegment, interest: '', portfolio_size: '', message: '',
    callback_requested: callbackDefault, preferred_time: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [capEnabled, setCapEnabled] = useState(false);
  const [hp, setHp] = useState('');
  const capRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const compact = variant === 'compact';

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = compact
      ? ['name', 'email', 'company', 'phone']
      : ['name', 'email', 'company', 'phone', 'segment', 'portfolio_size', 'interest', 'message'];
    if (required.some((k) => !String(form[k] || '').trim())) {
      toast.error('Please complete all fields before submitting.');
      return;
    }
    if (!isValidPhone(form.phone)) {
      toast.error('Please enter a valid US, UK or Australian phone number.');
      return;
    }
    let capToken = '';
    if (capEnabled) {
      capToken = await capRef.current?.getToken();
      if (!capToken) {
        toast.error('Anti-spam check failed. Please try again.');
        return;
      }
    }
    setLoading(true);
    try {
      await createLead({
        ...form,
        source,
        language: lang,
        page: typeof window !== 'undefined' ? window.location.pathname : undefined,
        captcha_token: capToken,
        hp,
        ...getUtm(),
      });
      setDone(true);
      toast.success(t('form.successTitle'));
    } catch (err) {
      toast.error(t('form.errorBody'));
      capRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className={cn('flex flex-col items-center gap-4 rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-white/10 dark:bg-white/[0.03]', className)}>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-brand/10 text-brand">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h3 className="font-display text-xl font-bold text-slate-900 dark:text-white">{t('form.successTitle')}</h3>
        <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">{t('form.successBody')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className={cn('rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-white/[0.03] sm:p-7', className)}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className={compact ? 'sm:col-span-2' : ''}>
          <label className={labelBase} htmlFor="lf-name">{t('form.name')}</label>
          <input id="lf-name" data-testid="lf-name" className={inputBase} value={form.name} onChange={set('name')} placeholder={t('form.namePh')} required />
        </div>
        <div className={compact ? 'sm:col-span-2' : ''}>
          <label className={labelBase} htmlFor="lf-email">{t('form.email')}</label>
          <input id="lf-email" data-testid="lf-email" type="email" className={inputBase} value={form.email} onChange={set('email')} placeholder={t('form.emailPh')} required />
        </div>
        <div>
          <label className={labelBase} htmlFor="lf-company">{t('form.company')}</label>
          <input id="lf-company" data-testid="lf-company" className={inputBase} value={form.company} onChange={set('company')} placeholder={t('form.companyPh')} required />
        </div>
        <div>
          <label className={labelBase} htmlFor="lf-phone">{t('form.phone')}</label>
          <input id="lf-phone" data-testid="lf-phone" type="tel" className={inputBase} value={form.phone} onChange={set('phone')} placeholder="+1, +44 or +61 …" required />
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">US, UK or Australian number — include your country code (e.g. +1, +44, +61).</p>
        </div>

        {!compact && (
          <>
            <div>
              <label className={labelBase} htmlFor="lf-segment">{t('form.segment')}</label>
              <select id="lf-segment" data-testid="lf-segment" className={inputBase} value={form.segment} onChange={set('segment')} required>
                <option value="">{t('form.selectOption')}</option>
                {SEGMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelBase} htmlFor="lf-portfolio">{t('form.portfolioSize')}</label>
              <select id="lf-portfolio" data-testid="lf-portfolio" className={inputBase} value={form.portfolio_size} onChange={set('portfolio_size')} required>
                <option value="">{t('form.selectOption')}</option>
                {PORTFOLIO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelBase} htmlFor="lf-interest">{t('form.interest')}</label>
              <select id="lf-interest" data-testid="lf-interest" className={inputBase} value={form.interest} onChange={set('interest')} required>
                <option value="">{t('form.selectOption')}</option>
                {INTEREST_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelBase} htmlFor="lf-message">{t('form.message')}</label>
              <textarea id="lf-message" data-testid="lf-message" rows={4} className={inputBase} value={form.message} onChange={set('message')} placeholder={t('form.messagePh')} required />
            </div>
          </>
        )}
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-white/10 dark:bg-white/[0.03]">
        <label className="flex items-center gap-2.5 text-sm font-medium text-slate-700 dark:text-slate-200">
          <input type="checkbox" checked={form.callback_requested} onChange={(e) => setForm((f) => ({ ...f, callback_requested: e.target.checked }))} data-testid="lf-callback" className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand" />
          Request a callback — we&rsquo;ll send you a link to book a time
        </label>
        {form.callback_requested && (
          <div className="mt-3">
            <label className={labelBase} htmlFor="lf-preferred">Preferred time / timezone</label>
            <input id="lf-preferred" className={inputBase} value={form.preferred_time} onChange={set('preferred_time')} placeholder="e.g. Weekday mornings ET" data-testid="lf-preferred-time" />
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {/* honeypot — hidden from humans */}
        <input value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 opacity-0" />
        <CaptchaWidget ref={capRef} onEnabledChange={setCapEnabled} className="mt-1" />
        <Btn type="submit" size="lg" full disabled={loading} data-testid="lf-submit">
          {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> {t('common.sending')}</>) : (submitLabel || t('form.submitDemo'))}
        </Btn>
        <p className="text-center text-xs leading-relaxed text-slate-400 dark:text-slate-500">{t('form.consent')}</p>
      </div>
    </form>
  );
}
